/**
 * 크레딧 조건 설정 (운영자 통합 페이지)
 *
 *  환불·취소·만료·환산 기준 단가 등 운영 정책 조건을 한곳에서 관리.
 *
 *  화면 구성:
 *   1. 환산 / 기본 단가  — KRW_PER_CREDIT
 *   2. 결제 취소 / 환불 — REFUND_*
 *   3. 자동 지급 / 보너스 — SIGNUP_*, MONTHLY_*, PAYMENT_REFUND_AMOUNT, FREE_BURN_PRIORITY
 *   4. 만료 정책 — EXPIRE_DAYS_*, EXPIRE_NOTICE_*
 *   5. 휴면 / 탈퇴 — DORMANT_*, WITHDRAWAL_*
 *
 *  ※ 각 정책 행의 ⓘ 아이콘에 마우스를 올리면 상세 설명·예시·권장값 팝업이 표시됩니다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Pencil,
  RotateCcw,
  Coins,
  Receipt,
  Gift,
  Hourglass,
  Moon,
  History,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { creditApi, CreditPolicy, CreditPolicyChange } from '@/services/api/credit';

/* ─────────────────────────────────────────────────────────────
 *  섹션 정의 — 정책 키를 도메인별로 그룹핑 + 상세 설명·예시
 * ───────────────────────────────────────────────────────────── */

interface SectionItem {
  key: string;
  label: string;
  unit?: string;
  hint?: string;
  /** 정책이 무엇을 결정하는지 1~2줄 설명 */
  detail?: string;
  /** 구체적 예시 (값을 넣어서 시나리오 설명) */
  example?: string;
  /** 권장값 */
  recommended?: string;
}

interface Section {
  id: string;
  title: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  items: SectionItem[];
  /** 정확히 매칭 안 되더라도 prefix 로 흡수할 패턴 */
  matchPrefixes?: string[];
}

const SECTIONS: Section[] = [
  {
    id: 'rate',
    title: '환산 / 기본 단가',
    desc: '1 크레딧 ↔ 원화 환산 기준값입니다. 과거 거래는 거래시점 단가가 보존되어 영향받지 않습니다.',
    icon: Coins,
    items: [
      {
        key: 'KRW_PER_CREDIT',
        label: '1 크레딧 단가',
        unit: '원',
        hint: '예: 8.5',
        detail:
          '1 크레딧이 몇 원에 해당하는지 환산 기준값입니다. UI 환산 표시·신규 거래 금액 계산·환불 단가의 기본값으로 사용됩니다.',
        example:
          '8.5 로 설정 시 → 100 크레딧 사용 = ₩850 으로 화면 표시. 사장님이 30,000 크레딧 충전 = ₩255,000 결제. 단가를 9로 변경해도 과거 거래는 거래시점 8.5 가 보존됩니다 (HIST_COMPANY_CREDIT_TX.PURCHASE_PRICE_KRW).',
        recommended: '8.5',
      },
    ],
  },
  {
    id: 'ticket-offline',
    title: '이용권 오프라인 수동발급',
    desc: '사장님이 매장에서 보호자에게 직접 수동발급할 때 차감되는 크레딧 정책 (PG 미경유, 운영 수수료만 적용).',
    icon: Coins,
    items: [
      {
        key: 'MANUAL_TICKET_KRW_RATIO',
        label: '환산 비율 (수수료율)',
        unit: '%',
        detail:
          '발급 시 총금액에서 차감할 수수료 비율 (소수점 가능). 온라인 PG 4% 와 별개의 정책.',
        example:
          '0.9 → 500,000원 × 0.9% / 8.5 = 529 크 차감. 매장별 협의 시 조정 가능.',
        recommended: '0.9',
      },
      {
        key: 'MANUAL_TICKET_KRW_FLOOR',
        label: 'Fallback 기준 금액',
        unit: '원',
        detail:
          '이용권 총 거래액이 이 금액 미만이면 정상 환산을 무시하고 FLOOR_CREDITS 를 일괄 부과. 비정상 단가 보호 (놀이방·데이케어 1시간 5,000원이 최소 기준).',
        example:
          '5000 → 4,999원 이용권 발급 시 정책 일괄 부과(10크). 5,001원 이상은 정상 환산 (0.9%).',
        recommended: '5000',
      },
      {
        key: 'MANUAL_TICKET_FLOOR_CREDITS',
        label: 'Fallback 시 일괄 부과',
        unit: '크',
        detail:
          '총 금액이 KRW_FLOOR 미만일 때 일괄 차감되는 크레딧 양.',
        example:
          '10 → 4,999원 이용권 = 10크 일괄 차감 (정상 환산 대비 패널티).',
        recommended: '10',
      },
      {
        key: 'MANUAL_TICKET_REVERSAL_RATIO',
        label: '취소 시 환원 비율',
        unit: '%',
        detail:
          '발급 후 취소 시 차감된 크레딧을 사장님에게 돌려주는 비율. 100=전액·0=환원없음.',
        example:
          '100 → 529크 차감 → 취소 시 529크 환원. 50 → 264크 환원.',
        recommended: '100',
      },
      {
        key: 'MANUAL_TICKET_REVERSAL_WINDOW_DAYS',
        label: '환원 가능 기간',
        unit: '일',
        detail:
          '발급일로부터 N일 이내 취소만 환원. 0=무기한.',
        example:
          '30 → 발급 후 30일 이내 취소만 환원. 31일째는 환원 불가.',
        recommended: '30',
      },
    ],
  },
  {
    id: 'refund',
    title: '결제 취소 / 환불 조건',
    desc: '셀프 100% 취소 가능 기간·환불 위약금·최소 환불 금액 등 결제 사후 처리 정책입니다.',
    icon: Receipt,
    items: [
      {
        key: 'REFUND_WINDOW_FULL_DAYS',
        label: '100% 취소 가능 기간',
        unit: '일',
        hint: '결제일부터 N일 이내 + 미사용 시',
        detail:
          '사장님이 본인 결제를 직접 100% 취소할 수 있는 기간입니다. 충전 크레딧이 한 번도 사용되지 않은 경우에만 적용됩니다.',
        example:
          '7 로 설정 시 → 1/1 결제 후 1/7 까지 잔액 그대로면 셀프 취소 가능 (PG cancel + 잔액 회수). 1/2 라도 단 1 크레딧 사용했으면 일반 환불 절차로 진행. 14 로 늘리면 분쟁 케이스 감소하나 운영 부담 증가.',
        recommended: '7~14',
      },
      {
        key: 'REFUND_PENALTY_RATE',
        label: '기간 이후 환불 위약금',
        unit: '%',
        hint: '100% 기간 지난 환불 시 차감 비율',
        detail:
          '100% 취소 가능 기간이 지난 후 환불 시 잔여 금액에서 차감하는 비율입니다. 운영 비용 충당 + 무분별 환불 방지.',
        example:
          '10 (%) 으로 설정 + 잔액 10,000 크 (₩65,000) 환불 시 → 위약금 ₩6,500 차감 → 사장님께 ₩58,500 환불. 0 으로 설정하면 위약금 없이 전액 환불.',
        recommended: '0~10',
      },
      {
        key: 'REFUND_MIN_AMOUNT_KRW',
        label: '최소 환불 금액',
        unit: '원',
        hint: '이 금액 미만은 환불 거절',
        detail:
          '이 금액 미만의 환불 요청은 거절됩니다. PG 환불 수수료/내부 처리비를 못 메꿔 손해가 나는 케이스 차단.',
        example:
          '1,000 으로 설정 시 → 환불 가능액 ₩800 이면 거절 ("최소 환불 금액 미달"). ₩1,500 이면 환불 진행. 토스 PG 의 최소 환불 단위(₩100) 고려해서 설정.',
        recommended: '1,000',
      },
      {
        key: 'REFUND_AUTO_APPROVE_KRW',
        label: '자동 승인 한도',
        unit: '원',
        hint: '이 금액 이하 자동 처리',
        detail:
          '이 금액 이하 환불 요청은 관리자 검토 없이 자동 처리됩니다. 초과 시 운영자 큐로 이동.',
        example:
          '50,000 으로 설정 시 → ₩30,000 환불 요청 = 자동 승인 + 즉시 처리. ₩100,000 = 운영자 검토 큐로 이동 (사기성 환불 방지).',
        recommended: '30,000~100,000',
      },
      {
        key: 'REFUND_FREE_BALANCE_ALLOWED',
        label: '무료 잔액 환불 허용',
        hint: 'true/false',
        detail:
          '시스템이 무료로 지급한 크레딧 (가입 보너스/월 무료/이벤트 등) 도 환불 대상에 포함할지 여부.',
        example:
          'false (권장) → PG 충전한 PAID 잔액만 환불. 무료 분은 소멸. true → 사장님 환불 시 가입 보너스도 함께 환불 (현금 유출 위험 ↑).',
        recommended: 'false',
      },
    ],
  },
  {
    id: 'grant',
    title: '자동 지급 / 보너스',
    desc: '가입 보너스·매월 무료·결제 환급 등 시스템이 자동으로 지급하는 크레딧 정책입니다.',
    icon: Gift,
    items: [
      {
        key: 'SIGNUP_BONUS_AMOUNT',
        label: '신규 가입 보너스',
        unit: '크레딧',
        detail:
          '사장님이 신규 가입할 때 1회 자동 지급되는 무료 크레딧 양입니다.',
        example:
          '500 으로 설정 시 → 가입 즉시 500 크레딧 (₩3,250 가치) 자동 충전 + 사장님에게 알림. 신규 사장님 유치 효과 ↔ 운영 비용 트레이드오프.',
        recommended: '300~1,000',
      },
      {
        key: 'SIGNUP_BONUS_EXPIRE_DAYS',
        label: '가입 보너스 만료',
        unit: '일',
        detail:
          '가입 보너스 크레딧이 지급일로부터 N일 후 자동 만료됩니다. 만료 알림은 EXPIRE_NOTICE_DAYS_BEFORE 정책 적용.',
        example:
          '60 으로 설정 시 → 1/1 가입자의 보너스는 3/2 자동 소멸. 0 으로 두면 무기한 (전금법상 PAID 가 아니라 무료라 가능하나 비권장).',
        recommended: '60~90',
      },
      {
        key: 'MONTHLY_FREE_AMOUNT',
        label: '매월 자동 무료 지급',
        unit: '크레딧',
        detail:
          '매월 1일 00:00 배치로 모든 활성 사업장에 자동 지급되는 무료 크레딧.',
        example:
          '100 으로 설정 + 활성 사업장 1,000곳 → 매월 100,000 크레딧 (₩650,000 가치) 무상 분배. 활성 사장님 유지 효과 / 비용은 monthly 누적.',
        recommended: '100~300',
      },
      {
        key: 'MONTHLY_FREE_EXPIRE_DAYS',
        label: '월 무료 만료',
        unit: '일',
        hint: '0 = 그 달 말일 자동 만료',
        detail:
          '월 자동 지급분의 만료 일수. 0 = 특수 처리 (그 달 말일에 모두 소멸).',
        example:
          '0 → 1/1 지급분 1/31 23:59:59 만료. 30 → 1/1 지급분은 1/31 만료 (사실상 동일). 60 → 2/29까지 사용 가능 (이월 효과).',
        recommended: '0 (그 달 내 소진 유도)',
      },
      {
        key: 'PAYMENT_REFUND_AMOUNT',
        label: '결제 거래 환급',
        unit: '크레딧/거래',
        detail:
          '사장님이 PG 결제로 충전할 때마다 추가로 지급되는 보너스 크레딧 (충전 1회당).',
        example:
          '10 으로 설정 시 → 사장님이 30,000 크레딧 충전하면 30,000 + 10 = 30,010 크 적립. 충전 빈도 유도용 인센티브.',
        recommended: '10~50',
      },
      {
        key: 'REFERRAL_BONUS_AMOUNT',
        label: '추천인 보상',
        unit: '크레딧',
        detail:
          '추천 코드로 가입한 사장님이 첫 결제 완료 시 추천인에게 지급되는 보상.',
        example:
          '200 으로 설정 시 → A 사장님이 추천 코드로 B 사장님 초대 → B 가 첫 PG 결제 완료 시 A 에게 200 크레딧 자동 지급.',
        recommended: '100~500',
      },
      {
        key: 'FREE_BURN_PRIORITY',
        label: '차감 우선순위',
        hint: 'FREE_FIRST / PAID_FIRST',
        detail:
          '크레딧 사용 시 무료 잔액과 PG 충전 잔액 중 어느 것부터 먼저 차감할지 결정.',
        example:
          'FREE_FIRST (권장) → 가입 보너스/월 무료부터 소진 → PG 충전 잔액 오래 보존 → 셀프 취소 가능 기간 활용도 ↑.\nPAID_FIRST → 충전 잔액 먼저 차감 → 셀프 취소 거의 불가 (무료가 남아도 PG Lot 이 사용됨).',
        recommended: 'FREE_FIRST',
      },
    ],
  },
  {
    id: 'expire',
    title: '만료 정책',
    desc: '지급 유형별 크레딧 만료 기간입니다. 0 = 무기한.',
    icon: Hourglass,
    items: [
      {
        key: 'EXPIRE_DAYS_GRANT_SIGNUP',
        label: '가입 보너스',
        unit: '일',
        detail: '가입 시 자동 지급분의 만료 기간.',
        example: '90 → 1/1 지급분은 3/31 자동 소멸. 사장님이 빨리 활용하도록 유도.',
        recommended: '60~90',
      },
      {
        key: 'EXPIRE_DAYS_GRANT_MONTHLY_FREE',
        label: '월 무료',
        unit: '일',
        hint: '0 = 그 달 말일',
        detail: '매월 자동 지급분의 만료 기간. 0 은 그 달 말일로 특수 처리.',
        example: '0 (권장) → 1/1 지급 = 1/31 만료. 다음 달 1일에 새로 받음. 누적 방지.',
        recommended: '0',
      },
      {
        key: 'EXPIRE_DAYS_GRANT_PROMOTION',
        label: '프로모션 보너스',
        unit: '일',
        detail: '이벤트/프로모션으로 지급되는 크레딧의 만료 기간.',
        example: '60 → 이벤트 종료 후 60일까지 사용 가능. 단기 이벤트는 30일로 짧게.',
        recommended: '30~60',
      },
      {
        key: 'EXPIRE_DAYS_GRANT_REFERRAL',
        label: '추천인 보상',
        unit: '일',
        detail: '추천인 코드 보상 크레딧의 만료 기간.',
        example: '180 → 비교적 길게 (추천한 사람이 활용할 시간 충분히 제공).',
        recommended: '180',
      },
      {
        key: 'EXPIRE_DAYS_GRANT_OPERATION',
        label: '관리자 운영 발급',
        unit: '일',
        detail: '운영팀이 수동으로 지급한 이벤트성/CS 크레딧의 만료 기간.',
        example: '60 → 5/1 운영팀이 사장님께 사과 크레딧 100 지급 → 6/30 만료.',
        recommended: '60~90',
      },
      {
        key: 'EXPIRE_DAYS_GRANT_BUG_COMPENSATION',
        label: '버그 보상',
        unit: '일',
        hint: '0 = 무기한',
        detail:
          '시스템 장애/버그 보상으로 지급한 크레딧의 만료 기간. 회사 귀책이므로 0(무기한) 권장.',
        example:
          '0 (권장) → 장애 보상은 영구 보존. 사장님 입장에서 "회사가 잘못해서 준 건데 만료시키는 건 부당" 클레임 방지.',
        recommended: '0',
      },
      {
        key: 'EXPIRE_DAYS_GRANT_PAYMENT_REWARD',
        label: '결제 환급',
        unit: '일',
        detail: '결제 거래 환급(PAYMENT_REFUND_AMOUNT)으로 지급된 크레딧의 만료 기간.',
        example: '180 → 충전 시점부터 6개월. 충전 빈도 유지 인센티브.',
        recommended: '180',
      },
      {
        key: 'EXPIRE_DAYS_PURCHASE',
        label: 'PG 결제 충전',
        unit: '일',
        hint: '전금법 §19 — 0(무기한) 유지',
        detail:
          '사장님이 돈을 지불하고 충전한 크레딧의 만료 기간. 전금법상 선불충전금은 미사용시 환급 의무가 있어 만료 불가.',
        example: '0 (필수) → 사장님이 충전한 크레딧은 영구 유효. 만료시키면 전금법 위반.',
        recommended: '0 (필수)',
      },
      {
        key: 'EXPIRE_DAYS_PURCHASE_MANUAL',
        label: '결제 누락 보정',
        unit: '일',
        detail:
          'PG 결제는 됐지만 시스템 오류로 미적립된 케이스를 관리자가 사후 보정 시 지급.',
        example: '0 (권장) → PURCHASE 와 동일하게 무기한 (실질적으로 PG 결제분이므로).',
        recommended: '0',
      },
      {
        key: 'EXPIRE_NOTICE_DAYS_BEFORE',
        label: '만료 1차 알림',
        unit: '일 전',
        detail: '크레딧 만료 N일 전 사장님께 SMS/푸시로 첫 안내 발송.',
        example: '7 → 만료 7일 전 알림 "보너스 100 크레딧이 1/8 만료됩니다".',
        recommended: '7',
      },
      {
        key: 'EXPIRE_NOTICE_DAYS_BEFORE_2ND',
        label: '만료 2차 알림',
        unit: '일 전',
        detail: '만료 직전 재차 안내. 1차 알림 후 미사용 케이스 리텐션.',
        example: '1 → 만료 1일 전 마지막 알림 "내일 소멸됩니다".',
        recommended: '1',
      },
    ],
    matchPrefixes: ['EXPIRE_DAYS_', 'EXPIRE_NOTICE_'],
  },
  {
    id: 'dormant',
    title: '휴면 / 탈퇴 처리',
    desc: '장기 미사용 사업장 휴면 처리 + 탈퇴 시 잔액 처리 정책 (전금법 §19 대응).',
    icon: Moon,
    items: [
      {
        key: 'DORMANT_YEARS_INACTIVE',
        label: '휴면 진입 기준',
        unit: '년',
        detail:
          '마지막 활동(잔액 변동) 이후 N년 미사용 시 사업장 지갑을 휴면 처리. 전금법 §19 대응.',
        example: '5 (법정 기준) → 2021/1/1 이후 무활동 사장님은 2026/1/1 휴면 진입.',
        recommended: '5 (법정 기준)',
      },
      {
        key: 'DORMANT_NOTICE_DAYS_BEFORE',
        label: '휴면 사전 안내',
        unit: '일 전',
        detail: '휴면 진입 N일 전에 사장님께 SMS/카카오톡으로 안내 발송.',
        example: '30 → 휴면 30일 전 알림. 사장님이 로그인하면 활동으로 간주되어 카운터 리셋.',
        recommended: '30',
      },
      {
        key: 'DORMANT_REFUND_AUTO_ATTEMPT',
        label: '휴면 진입 시 자동 환급',
        hint: 'true/false',
        detail: '휴면 처리 시 PAID 잔액을 사장님 계좌로 자동 환급 시도할지 여부.',
        example:
          'true → 휴면 처리 = 자동 환급 시도 (계좌 정보 있으면). 실패하면 별도 큐로 이동.\nfalse → 사장님이 재로그인해서 직접 환급 신청해야 함.',
        recommended: 'true',
      },
      {
        key: 'DORMANT_REFUND_PENALTY_RATE',
        label: '휴면 환급 위약금',
        unit: '%',
        detail: '휴면 자동 환급 시 잔액에서 차감하는 비율.',
        example: '0 (권장) → 위약금 없이 전액 환급. 전금법 정신상 0 권장.',
        recommended: '0',
      },
      {
        key: 'DORMANT_NOTICE_CHANNELS',
        label: '휴면 안내 채널',
        hint: 'CSV (SMS,KAKAO_ALIM)',
        detail: '휴면 진입 알림에 사용할 채널들 (CSV).',
        example: 'SMS,KAKAO_ALIM → SMS + 카카오 알림톡 둘 다 발송. 카카오만 쓰려면 KAKAO_ALIM.',
        recommended: 'SMS,KAKAO_ALIM',
      },
      {
        key: 'WITHDRAWAL_BLOCK_IF_PAID',
        label: '유료 잔액 시 탈퇴 차단',
        hint: 'true/false',
        detail:
          '사장님이 회원 탈퇴할 때 PG 충전 잔액이 남아있으면 탈퇴를 차단할지 여부. 분쟁 예방.',
        example:
          'true → "환불 받으신 후 탈퇴해 주세요" 안내 + 탈퇴 버튼 비활성화. 차후 클레임 방지.',
        recommended: 'true',
      },
      {
        key: 'WITHDRAWAL_FREE_FORFEIT',
        label: '탈퇴 시 무료 잔액 소멸',
        hint: 'true/false',
        detail: '탈퇴 시 가입 보너스 등 무료 크레딧을 소멸시킬지 여부.',
        example: 'true (권장) → 탈퇴 처리 시 무료 잔액 0 으로 회수 (운영 비용 보호).',
        recommended: 'true',
      },
    ],
    matchPrefixes: ['DORMANT_', 'WITHDRAWAL_'],
  },
];

/* ─────────────────────────────────────────────────────────────
 *  Helper — 정책 키 검색
 * ───────────────────────────────────────────────────────────── */

const findByKey = (list: CreditPolicy[], key: string): CreditPolicy | undefined =>
  list.find((p) => p.policyKey === key);

const collectSectionPolicies = (
  section: Section,
  all: CreditPolicy[],
): { item: SectionItem; policy: CreditPolicy | undefined }[] => {
  const known = section.items.map((item) => ({
    item,
    policy: findByKey(all, item.key),
  }));
  if (!section.matchPrefixes?.length) return known;

  const knownKeys = new Set(section.items.map((i) => i.key));
  const extras = all
    .filter(
      (p) =>
        !knownKeys.has(p.policyKey) &&
        section.matchPrefixes!.some((prefix) => p.policyKey.startsWith(prefix)),
    )
    .map(
      (policy) =>
        ({
          item: { key: policy.policyKey, label: policy.policyKey, hint: policy.description },
          policy,
        }) satisfies { item: SectionItem; policy: CreditPolicy },
    );
  return [...known, ...extras];
};

const collectOrphan = (all: CreditPolicy[]): CreditPolicy[] => {
  const claimedKeys = new Set<string>();
  for (const s of SECTIONS) {
    s.items.forEach((i) => claimedKeys.add(i.key));
    if (s.matchPrefixes?.length) {
      for (const p of all) {
        if (s.matchPrefixes.some((prefix) => p.policyKey.startsWith(prefix))) {
          claimedKeys.add(p.policyKey);
        }
      }
    }
  }
  return all.filter((p) => !claimedKeys.has(p.policyKey));
};

const formatValue = (p: CreditPolicy | undefined): string => {
  if (!p) return '—';
  if (p.cdDataType === 'BOOLEAN') return p.policyValue === 'true' ? '예' : '아니오';
  return p.policyValue;
};

/* ─────────────────────────────────────────────────────────────
 *  InfoTip — ⓘ 아이콘 호버 시 상세 설명·예시·권장값 팝업
 * ───────────────────────────────────────────────────────────── */

interface InfoTipProps {
  item: SectionItem;
}

function InfoTip({ item }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 클릭 외부 닫기 (모바일 대응)
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!item.detail && !item.example) return null;

  return (
    <div
      ref={ref}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-orange-500 hover:bg-orange-50 focus:outline-none focus:text-orange-600"
        aria-label={`${item.label} 설명 보기`}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute left-5 top-1/2 -translate-y-1/2 z-30 w-[320px] rounded-lg border border-gray-200 bg-white shadow-lg p-3 text-xs space-y-2 pointer-events-none">
          <div className="font-bold text-gray-900 text-sm border-b border-gray-100 pb-1">
            {item.label}
            {item.unit && <span className="text-gray-500 font-normal ml-1">({item.unit})</span>}
          </div>
          {item.detail && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">설명</div>
              <div className="text-gray-700 leading-relaxed mt-0.5">{item.detail}</div>
            </div>
          )}
          {item.example && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-orange-600 font-semibold">예시</div>
              <div className="text-gray-700 leading-relaxed mt-0.5 whitespace-pre-line">
                {item.example}
              </div>
            </div>
          )}
          {item.recommended && (
            <div className="bg-green-50 border border-green-200 rounded px-2 py-1">
              <span className="text-[10px] uppercase tracking-wide text-green-700 font-semibold mr-1">
                권장
              </span>
              <span className="text-green-800 font-mono font-semibold">{item.recommended}</span>
            </div>
          )}
          <div className="font-mono text-[10px] text-gray-400 pt-1 border-t border-gray-100">
            {item.key}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  Page
 * ───────────────────────────────────────────────────────────── */

export function CreditConditionsPage() {
  const [policies, setPolicies] = useState<CreditPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ policy: CreditPolicy; item: SectionItem } | null>(null);
  const [changes, setChanges] = useState<CreditPolicyChange[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [pList, cList] = await Promise.all([
        creditApi.listPolicies(),
        creditApi.listChanges(20).catch(() => [] as CreditPolicyChange[]),
      ]);
      setPolicies(pList);
      setChanges(cList.filter((c) => c.targetType === 'POLICY'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ratePolicy = useMemo(() => findByKey(policies, 'KRW_PER_CREDIT'), [policies]);
  const refundWindow = useMemo(() => findByKey(policies, 'REFUND_WINDOW_FULL_DAYS'), [policies]);
  const orphans = useMemo(() => collectOrphan(policies), [policies]);

  return (
    <div className="p-6 space-y-6 max-w-[1280px]">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">크레딧 조건 설정</h1>
        <p className="mt-1 text-sm text-gray-500">
          환불·취소·만료·환산 기준 단가 조건을 한곳에서 관리합니다. 변경 즉시 적용되며 이력은 자동 기록됩니다.
        </p>
        <p className="mt-1 text-xs text-orange-700">
          💡 정책명 옆 ⓘ 아이콘에 마우스를 올리면 상세 설명·예시·권장값을 확인할 수 있습니다.
        </p>
      </header>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard
          title="1 크레딧 단가"
          value={ratePolicy ? `₩ ${ratePolicy.policyValue}` : '미설정'}
          subtitle="환산 / UI 표시 기준"
          accent="primary"
          onClick={
            ratePolicy
              ? () =>
                  setEditing({
                    policy: ratePolicy,
                    item: SECTIONS[0].items[0],
                  })
              : undefined
          }
        />
        <SummaryCard
          title="100% 취소 가능 기간"
          value={refundWindow ? `${refundWindow.policyValue} 일` : '미설정'}
          subtitle="결제일 + 미사용 조건"
          accent="info"
        />
        <SummaryCard
          title="총 등록 정책"
          value={`${policies.length} 건`}
          subtitle={`최근 변경 ${changes.length} 건`}
          accent="muted"
        />
      </div>

      {/* 본문 — 좌측 섹션 / 우측 변경 이력 */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          {loading && <div className="text-sm text-gray-500">불러오는 중…</div>}
          {!loading &&
            SECTIONS.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                policies={policies}
                onEdit={(item, policy) =>
                  policy ? setEditing({ policy, item }) : undefined
                }
              />
            ))}

          {!loading && orphans.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-bold text-amber-900 mb-2">
                기타 (섹션 미분류) — {orphans.length}건
              </h3>
              <ul className="space-y-1 text-xs text-amber-900">
                {orphans.map((p) => (
                  <li key={p.policySeq} className="flex justify-between">
                    <span className="font-mono">{p.policyKey}</span>
                    <span>{formatValue(p)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700 mt-2">
                이 정책들은 코드에서 정의되지 않은 신규 키입니다. 필요하면 본 페이지의 SECTIONS 정의에 추가하세요.
              </p>
            </div>
          )}
        </div>

        {/* 변경 이력 사이드 */}
        <aside className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 inline-flex items-center gap-2">
              <History className="w-4 h-4 text-gray-500" />
              최근 변경 이력
            </h3>
            <Button variant="ghost" size="sm" onClick={load} title="새로고침">
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
          {changes.length === 0 ? (
            <div className="p-4 text-xs text-gray-500">변경 이력이 없습니다.</div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {changes.map((c) => (
                <li key={c.changeSeq} className="px-4 py-3 text-xs">
                  <div className="font-mono text-gray-900 truncate">{c.targetLabel}</div>
                  <div className="text-gray-600 mt-1 line-clamp-2">{c.reason}</div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {c.adminName ?? `#${c.adminSeq}`} · {new Date(c.dtRegist).toLocaleString('ko-KR')}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {/* 변경 모달 */}
      {editing && (
        <EditModal
          policy={editing.policy}
          item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  Summary Card
 * ───────────────────────────────────────────────────────────── */

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle: string;
  accent: 'primary' | 'info' | 'muted';
  onClick?: () => void;
}

function SummaryCard({ title, value, subtitle, accent, onClick }: SummaryCardProps) {
  const styles = {
    primary: 'border-orange-200 bg-orange-50',
    info: 'border-blue-200 bg-blue-50',
    muted: 'border-gray-200 bg-gray-50',
  }[accent];
  const valueColor = {
    primary: 'text-orange-700',
    info: 'text-blue-700',
    muted: 'text-gray-800',
  }[accent];
  return (
    <div
      className={`rounded-lg border ${styles} p-4 ${onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="text-xs text-gray-600">{title}</div>
      <div className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
      {onClick && (
        <div className="mt-2 text-[11px] text-gray-600 inline-flex items-center gap-1">
          <Pencil className="w-3 h-3" /> 클릭하여 변경
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  Section Card
 * ───────────────────────────────────────────────────────────── */

interface SectionCardProps {
  section: Section;
  policies: CreditPolicy[];
  onEdit: (item: SectionItem, policy: CreditPolicy | undefined) => void;
}

function SectionCard({ section, policies, onEdit }: SectionCardProps) {
  const Icon = section.icon;
  const rows = collectSectionPolicies(section, policies);

  return (
    <section className="rounded-lg border border-gray-200 bg-white overflow-visible">
      <header className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h2 className="text-base font-bold text-gray-900 inline-flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-600" />
          {section.title}
        </h2>
        <p className="text-xs text-gray-500 mt-1">{section.desc}</p>
      </header>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-gray-500 bg-gray-50/50">
          <tr>
            <th className="text-left px-5 py-2 w-1/3">정책</th>
            <th className="text-left px-3 py-2">키</th>
            <th className="text-right px-3 py-2 w-32">현재 값</th>
            <th className="px-3 py-2 w-20"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(({ item, policy }) => (
            <tr key={item.key} className={!policy ? 'bg-red-50/30' : ''}>
              <td className="px-5 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-gray-900">{item.label}</span>
                  <InfoTip item={item} />
                </div>
                {item.hint && <div className="text-[11px] text-gray-500 mt-0.5">{item.hint}</div>}
              </td>
              <td className="px-3 py-3 font-mono text-[11px] text-gray-500">{item.key}</td>
              <td className="px-3 py-3 text-right">
                {policy ? (
                  <span className="font-semibold text-gray-900">
                    {formatValue(policy)}
                    {item.unit && <span className="text-xs text-gray-500 ml-1">{item.unit}</span>}
                  </span>
                ) : (
                  <span className="text-xs text-red-600">미설정</span>
                )}
              </td>
              <td className="px-3 py-3 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!policy}
                  onClick={() => onEdit(item, policy)}
                  title={policy ? '값 변경' : '시드 필요'}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  Edit Modal
 * ───────────────────────────────────────────────────────────── */

interface EditModalProps {
  policy: CreditPolicy;
  item: SectionItem;
  onClose: () => void;
  onSaved: () => void;
}

function EditModal({ policy, item, onClose, onSaved }: EditModalProps) {
  const [value, setValue] = useState(policy.policyValue);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isBoolean = policy.cdDataType === 'BOOLEAN';
  const isNumeric = policy.cdDataType === 'INT' || policy.cdDataType === 'DECIMAL';

  const canSubmit =
    value.trim().length > 0 &&
    value !== policy.policyValue &&
    reason.trim().length >= 5;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await creditApi.updatePolicy(policy.policyKey, value.trim(), reason.trim());
      onSaved();
    } catch (e) {
      console.error('정책 변경 실패', e);
      alert('정책 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`정책 변경 — ${item.label}`}>
      <div className="space-y-4">
        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-500">정책 키</span>
            <span className="font-mono text-gray-900">{policy.policyKey}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-500">데이터 타입</span>
            <span className="text-gray-900">{policy.cdDataType}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-500">최종 수정</span>
            <span className="text-gray-900">{new Date(policy.dtUpdate).toLocaleString('ko-KR')}</span>
          </div>
        </div>

        {/* 정책 설명 — 모달 안에서도 컨텍스트 제공 */}
        {(item.detail || item.example) && (
          <div className="space-y-1.5 bg-blue-50 border border-blue-200 rounded p-3 text-xs">
            {item.detail && <p className="text-blue-900 leading-relaxed">{item.detail}</p>}
            {item.example && (
              <p className="text-blue-800 leading-relaxed whitespace-pre-line">
                <span className="font-semibold">예시: </span>
                {item.example}
              </p>
            )}
            {item.recommended && (
              <p className="text-green-700">
                <span className="font-semibold">권장: </span>
                <span className="font-mono">{item.recommended}</span>
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">현재 값</label>
          <div className="font-mono text-gray-900 bg-gray-100 rounded px-3 py-2">
            {policy.policyValue}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">
            새 값 <span className="text-red-500">*</span>
            {item.unit && <span className="text-gray-500 font-normal ml-1">({item.unit})</span>}
          </label>
          {isBoolean ? (
            <div className="flex gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={value === 'true'}
                  onChange={() => setValue('true')}
                />
                예 (true)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={value === 'false'}
                  onChange={() => setValue('false')}
                />
                아니오 (false)
              </label>
            </div>
          ) : (
            <input
              type={isNumeric ? 'number' : 'text'}
              step={policy.cdDataType === 'DECIMAL' ? '0.01' : '1'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={item.hint}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
            />
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">
            변경 사유 <span className="text-red-500">*</span>
            <span className="text-gray-500 font-normal ml-1">(5자 이상)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 결제 분쟁 증가로 셀프 취소 기간을 14일로 연장"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400 resize-none"
          />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
          ⚠ 변경 즉시 적용됩니다. 과거 거래에는 영향이 없으며 (거래시점 값 보존),
          변경 이력은 HIST_CREDIT_POLICY_CHANGE 에 자동 기록됩니다.
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? '저장 중…' : '저장'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
